import { AlertCircle, AlertTriangle, CheckCircle2 } from 'lucide-react';
import * as React from 'react';

import { cn } from '@heidi/utils';
import { TierPracticeFilled, TierProFilled } from '../icons';

import { useIsExperimentalDesignSystemForms } from '../hooks/use-is-experimental-design-system-forms';
import { framedIconVariantsExperimental } from './framed-icon.experimental.styles';
import { framedIconVariants, iconSizeMap } from './framed-icon.styles';
import type {
  FramedIconColor,
  FramedIconElement,
  FramedIconProps,
  FramedIconState,
} from './framed-icon.types';

// Lucide and Heidi icons share `size`/`aria-hidden`; `any` unifies them without leaking each library's prop shape.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type IconLike = React.ComponentType<any>;

const defaultIcons: Partial<Record<FramedIconState, IconLike>> = {
  error: AlertTriangle,
  warning: AlertCircle,
  success: CheckCircle2,
  pro: TierProFilled,
  practice: TierPracticeFilled,
};

const FramedIcon = React.forwardRef<HTMLDivElement, FramedIconProps>(
  (
    { size = 'md', className, 'data-testid': testId, 'aria-label': ariaLabel, ...rest },
    ref
  ) => {
    const { state, color, icon, ...divProps } = rest as {
      state?: FramedIconState;
      color?: FramedIconColor;
      icon?: FramedIconElement;
    } & Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>;
    const dim = iconSizeMap[size];

    // `color` (entity frame) and `state` (status/tier frame) are mutually
    // exclusive. When `color` is set there is no default glyph — the types
    // require `icon` — and `state` must not fall back to neutral.
    const resolvedState: FramedIconState | undefined = color
      ? undefined
      : (state ?? 'neutral');

    let rendered: React.ReactNode;
    if (icon && React.isValidElement(icon)) {
      rendered = React.cloneElement(icon, {
        size: icon.props.size ?? dim,
        'aria-hidden': true,
      });
    } else {
      const Fallback = resolvedState ? defaultIcons[resolvedState] : undefined;
      rendered = Fallback ? <Fallback size={dim} aria-hidden /> : null;
    }

    const isExperimentalForms = useIsExperimentalDesignSystemForms();
    const variants = isExperimentalForms
      ? framedIconVariantsExperimental
      : framedIconVariants;
    return (
      <div
        ref={ref}
        data-testid={testId}
        data-experimental-form={isExperimentalForms}
        className={cn(
          variants(color ? { size, color } : { size, state: resolvedState }),
          className
        )}
        // When `aria-label` is provided we expose the frame as a single accessible image; otherwise the inner icon stays decorative.
        role={ariaLabel ? 'img' : undefined}
        aria-label={ariaLabel}
        {...divProps}
      >
        {rendered}
      </div>
    );
  }
);
FramedIcon.displayName = 'FramedIcon';

export { FramedIcon };
